import { usePageRefresh } from '../../../context/PageRefreshContext';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getMemberPayments,
    getMemberPaymentDetails,
} from '../../../redux/actions/SocietyPortal/MemberPortalAction/MemberPortalAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import Pagination from '../../../components/common/Pagination';
import {
    RECEIPT_STATUS_STYLE,
    PAYMENT_MODE_LABEL,
    fmtCurrency,
    fmtDate,
    humanize,
} from './memberConstants';

const ITEMS_PER_PAGE = 10;

// Receipts issued against this member's flats. Expanding a row pulls the
// receipt detail, which carries the invoice allocations — so the member can see
// which bill each payment was applied to without leaving the page.
const MyPayments = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        paymentsData,
        paymentsLoading,
        paymentsPagination,
        paymentDetailsData,
        paymentDetailsLoading,
    } = useSelector((state) => state.MemberPortalReducer || {});

    const [page, setPage] = useState(1);
    const [expandedId, setExpandedId] = useState(null);

    const fetchReceipts = useCallback(() => {
        if (!societyId) return;
        dispatch(getMemberPayments({ societyId, page, limit: ITEMS_PER_PAGE }));
    }, [dispatch, societyId, page]);
    usePageRefresh(fetchReceipts);

    useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

    const rows = useMemo(() => (Array.isArray(paymentsData) ? paymentsData : []), [paymentsData]);
    const totalPages = paymentsPagination?.totalPages || 1;
    const totalItems = paymentsPagination?.total ?? rows.length;

    const clearedTotal = rows
        .filter((r) => r.status === 'CLEARED')
        .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);

    const toggle = (receiptId) => {
        if (expandedId === receiptId) {
            setExpandedId(null);
            return;
        }
        setExpandedId(receiptId);
        dispatch(getMemberPaymentDetails({ id: receiptId, societyId }));
    };

    // Only trust the loaded detail when it is the row the member just opened —
    // otherwise a slow response could paint allocations under the wrong receipt.
    const allocationsFor = (receiptId) => {
        if (paymentDetailsData?.receipt_id !== receiptId) return null;
        return Array.isArray(paymentDetailsData.invoiceAllocations)
            ? paymentDetailsData.invoiceAllocations
            : [];
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">

                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div>
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="mdi:receipt-text-check-outline" className="tw-text-emerald-600" />
                            My Payments
                        </h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            Receipts the society office has issued against your flat
                        </p>
                    </div>
                    {clearedTotal > 0 && (
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-full tw-text-[11px] tw-font-semibold tw-bg-emerald-50 tw-text-emerald-700 tw-border tw-border-emerald-100">
                            <Icon icon="mdi:check-circle-outline" className="tw-text-sm" />
                            {fmtCurrency(clearedTotal)} cleared on this page
                        </span>
                    )}
                </div>

                <div className="tw-p-4">
                    {paymentsLoading && (
                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-12">
                            <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-emerald-500" />
                            <span className="tw-text-xs tw-text-gray-400">Loading...</span>
                        </div>
                    )}

                    {!paymentsLoading && rows.length === 0 && (
                        <div className="tw-text-center tw-py-14">
                            <Icon icon="mdi:receipt-text-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                            <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No payments recorded yet</p>
                        </div>
                    )}

                    {!paymentsLoading && rows.length > 0 && (
                        <>
                            <div className="tw-space-y-2">
                                {rows.map((r) => {
                                    const open = expandedId === r.receipt_id;
                                    const allocations = open ? allocationsFor(r.receipt_id) : null;
                                    return (
                                        <div key={r.receipt_id} className="tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
                                            <button
                                                onClick={() => toggle(r.receipt_id)}
                                                className="tw-w-full tw-text-left tw-p-3 hover:tw-bg-slate-50/60 tw-flex tw-items-center tw-gap-3"
                                            >
                                                <div className="tw-flex-1 tw-min-w-0">
                                                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                                                        <span className="tw-text-[11px] tw-font-mono tw-font-semibold tw-text-gray-700">
                                                            {r.receipt_number || '-'}
                                                        </span>
                                                        <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${RECEIPT_STATUS_STYLE[r.status] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                            {humanize(r.status)}
                                                        </span>
                                                    </div>
                                                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                                        {fmtDate(r.receipt_date)}
                                                        {' · '}
                                                        {PAYMENT_MODE_LABEL[r.payment_mode] || r.payment_mode || '-'}
                                                        {r.reference_no ? ` · ${r.reference_no}` : ''}
                                                    </p>
                                                </div>
                                                <span className="tw-text-sm tw-font-bold tw-text-emerald-600 tw-flex-shrink-0">
                                                    {fmtCurrency(r.amount)}
                                                </span>
                                                <Icon
                                                    icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                                                    className="tw-text-gray-400 tw-text-lg tw-flex-shrink-0"
                                                />
                                            </button>

                                            {open && (
                                                <div className="tw-border-t tw-border-gray-100 tw-bg-gray-50/60 tw-px-3 tw-py-3">
                                                    <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500 tw-font-semibold tw-mb-1.5">
                                                        Applied to
                                                    </p>
                                                    {paymentDetailsLoading && allocations === null && (
                                                        <p className="tw-text-[11px] tw-text-gray-400">Loading allocations...</p>
                                                    )}
                                                    {allocations !== null && allocations.length === 0 && (
                                                        <p className="tw-text-[11px] tw-text-gray-500">
                                                            This payment is on account and hasn't been applied to a bill yet.
                                                        </p>
                                                    )}
                                                    {allocations !== null && allocations.length > 0 && (
                                                        <ul className="tw-list-none tw-p-0 tw-m-0 tw-space-y-1">
                                                            {allocations.map((a, idx) => {
                                                                const doc = a.invoice_id
                                                                    ? (a.invoice?.document_number || `INV-${a.invoice_id}`)
                                                                    : (a.debitNote?.document_number || `DN-${a.debit_note_id}`);
                                                                return (
                                                                    <li
                                                                        key={a.allocation_id || idx}
                                                                        className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-[11px]"
                                                                    >
                                                                        <span className="tw-font-mono tw-text-gray-700">{doc}</span>
                                                                        <span className="tw-font-semibold tw-text-gray-800 tw-tabular-nums">
                                                                            {fmtCurrency(a.amount)}
                                                                        </span>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {totalPages > 1 && (
                                <div className="tw-pt-4 tw-mt-2 tw-border-t tw-border-gray-100">
                                    <Pagination
                                        currentPage={page}
                                        totalPages={totalPages}
                                        totalItems={totalItems}
                                        itemsPerPage={ITEMS_PER_PAGE}
                                        onPageChange={(p) => { setPage(p); setExpandedId(null); }}
                                        showItemsPerPage={false}
                                        itemLabel="receipts"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyPayments;
